import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { translate } from 'react-i18next'
import { connect } from 'react-redux'
import SelectField from 'component/Select/Select'
import Api from 'utils/ApiRequest'
import API from 'constants/api'

const DynamicSelectField = ({
  label,
  placeholder,
  value,
  onChange,
  required,
  errorText,
  showHelperArea,
  validateValue,
  user,
  disabled,
  className,
  fill,
  showAllOption,
  translate,
  disableLoading
}) => {
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const retryRef = useRef(0)
  const timeoutRef = useRef(null)

  useEffect(() => {
    if (!user?.dsToken) return

    retryRef.current = 0

    const fetchData = async () => {
      if (retryRef.current >= 3) return setLoading(false)

      const shouldShowLoading = !(disableLoading && value?.value)
      if (shouldShowLoading) setLoading(true)

      try {
        const api = new Api(user.dsToken)
        timeoutRef.current = setTimeout(() => {
          if (retryRef.current < 3) {
            retryRef.current++
            fetchData()
          }
        }, 5000)

        const response = await api.get(API.FETCH_PRICEBANNER_DYNAMICS)
        clearTimeout(timeoutRef.current)

        const data = Array.isArray(response?.data) ? response.data : []
        const mapped = data.map(item => ({ value: item.code, label: item.name }))
        const sorted = mapped.sort((a, b) => a.label.localeCompare(b.label))
        const final = showAllOption ? [{ label: translate('option.all'), value: 'todos' }, ...sorted] : sorted
        
        setOptions(final)
        retryRef.current = 0
      } catch (error) {
        clearTimeout(timeoutRef.current)
        if (retryRef.current < 3) {
          retryRef.current++
          fetchData()
        } else {
          setOptions([])
        }
      } finally {
        setLoading(false)
      }
    }

    fetchData()
    return () => clearTimeout(timeoutRef.current)
  }, [user?.dsToken, showAllOption, translate, disableLoading])

  const defaultLabel = label || translate('priceBannerHistoryAdapter.dynamic')
  const defaultPlaceholder = placeholder || translate('config.pricebannerConfig.form.cdDynamicLabel2')
  const placeholderLabel = (disableLoading || (!loading && options.length > 0)) ? defaultPlaceholder : translate('loading')

  return (
    <SelectField
      label={defaultLabel}
      placeholder={placeholderLabel}
      value={value}
      options={options}
      onChange={onChange}
      required={required}
      errorText={errorText || ''}
      showHelperArea={showHelperArea}
      validateValue={validateValue || ((val) => !required || (val && val.value))}
      disabled={disabled}
      className={className}
      fill={fill}
    />
  )
}

DynamicSelectField.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  errorText: PropTypes.string,
  showHelperArea: PropTypes.bool,
  validateValue: PropTypes.func,
  user: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  fill: PropTypes.bool,
  showAllOption: PropTypes.bool,
  translate: PropTypes.func.isRequired,
  disableLoading: PropTypes.bool,
}

const mapStateToProps = ({ auth }) => ({
  user: auth.user,
})

const DynamicSelectFieldTranslated = translate(null, {
  wait: true,
  translateFuncName: 'translate',
})(DynamicSelectField)

export default connect(mapStateToProps)(DynamicSelectFieldTranslated)

export { DynamicSelectField }
